import { Stack, Tooltip, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import { convertAmount, formatConvertedPrice, formatExchangeRate } from "utils";

import type { ReactElement } from "react";

interface Props {
  price: number | string | null;
  currencyCode: string | null;
  cadPerUsd: ExchangeRate | null;
  usdPerCad: ExchangeRate | null;
  children: ReactElement;
}

/**
 * Wraps a rendered price so hovering it shows the same amount in the other
 * currency.
 *
 * Strictly additive: the price it wraps is already correct and complete, and
 * never waits on the rate. When there is no usable rate — an empty registry, a
 * failed request, an amount that will not parse — the child renders on its own
 * with no tooltip, because an error next to a valid price reads as though the
 * price is wrong.
 *
 * Both directions are passed in rather than fetched here: a table mixes CAD and
 * USD bids, and the rates are requested once per view so the request count does
 * not grow with the number of rows.
 */
export function ConvertedPriceTooltip(props: Props) {
  const { price, currencyCode, cadPerUsd, usdPerCad, children } = props;
  const posthog = usePostHog();

  const exchangeRateByPriceCurrency: Record<string, ExchangeRate | null> = {
    USD: cadPerUsd,
    CAD: usdPerCad,
  };
  const exchangeRate = exchangeRateByPriceCurrency[currencyCode ?? ""] ?? null;

  if (price == null || exchangeRate?.rate == null) {
    return children;
  }

  const convertedAmount = convertAmount(price, exchangeRate.rate);
  if (convertedAmount === null) {
    return children;
  }

  const convertedPrice = formatConvertedPrice(
    convertedAmount,
    exchangeRate.quote_currency_code,
  );

  // The rate is named with its own date and never called today's: it is an
  // indicative daily average, it can be the previous business day's depending on
  // the viewer's timezone, and it is not the rate that applied when the quote
  // was made.
  const title = (
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        ≈ {convertedPrice}
      </Typography>
      <Typography variant="caption">
        {`Approximate, at the Bank of Canada rate of ${formatExchangeRate(exchangeRate.rate)} `}
        {`${exchangeRate.quote_currency_code} per ${exchangeRate.base_currency_code} `}
        {`on ${exchangeRate.rate_date}.`}
      </Typography>
    </Stack>
  );

  return (
    <Tooltip
      title={title}
      onOpen={() =>
        posthog?.capture(ClientAppEvent.QUOTE_CONVERTED_PRICE_TOOLTIP_OPENED, {
          base_currency_code: exchangeRate.base_currency_code,
          quote_currency_code: exchangeRate.quote_currency_code,
          rate_date: exchangeRate.rate_date,
        })
      }
    >
      {/* Tooltip clones its child to inject hover/focus handlers and a ref, so a
          child that is a plain function component silently never opens. Both
          price renderers are passed in from their own views and one of them
          (ShipmentPriceText) is exactly that, so the span makes the hover target
          a real DOM node rather than leaving each call site to remember. */}
      <span style={{ display: "inline-block", width: "fit-content" }}>
        {children}
      </span>
    </Tooltip>
  );
}
